import get from 'lodash/get';
import map from 'lodash/map';
import size from 'lodash/size';
import forEach from 'lodash/forEach';
import values from 'lodash/values';
import groupBy from 'lodash/groupBy';
import { computePageviewTypeFromState } from '../utils/pageviewTypes';
import SectionIds from './SectionIds';
import SectionTypes from './SectionTypes';
import { SERVICE_WORKER_VERSION } from '../constants';
import {
  getEventMetaData,
  getTrackingCode,
  getABTestData,
  getEventSection,
  getEventAdId,
  formattedSectionForAdImpressions,
  addEmptyFewResultsSectionToAdsGroupedBySectionId,
} from './utils';
import { TRACK_CATEGORIES, TRACK_ACTIONS, TRACK_LABELS } from './events';
import { VERTICAL_NAMES } from '../common/constants/common';
import { getCurrentPage } from '~/Serp/components/shared/Pager/selectors';
import { getFilters } from '~/Serp/selectors';

export const pageView = (action, prevState, nextState) => {
  // Avoid trigger tracking impression
  // when user press the browser back button
  const trovit = nextState.trovit;
  if (!trovit.canTriggerTrackingImpressions) {
    return null;
  }
  const config = nextState.config;

  const event = {
    hitType: 'pageview',
    page: trovit.url,
    eventMeta: {
      data: {
        id: trovit.pageviewId,
        type: 'page_views',
        attributes: {
          page: {
            type_id: computePageviewTypeFromState(nextState),
            referral_url: prevState && prevState.trovit.url,
          },
          site: {
            country_code: config.countryCode,
            vertical_id: config.verticalId,
          },
          search: {
            page_number: getCurrentPage(nextState),
          },
          user: {
            origin_id: trovit.origin,
            cookie_id: trovit.installationId,
            agent: trovit.userAgent,
            ip: trovit.userIp,
          },
          tracking: {
            ...getTrackingCode(trovit.origin, trovit.trackingCodes),
          },
          ...getABTestData(trovit.abTest),
        },
      },
    },
  };

  return event;
};

export const adImpressions = (action, prevState, nextState) => {
  // Avoid trigger tracking impression
  // when user press the browser back button
  if (!nextState.trovit.canTriggerTrackingImpressions) {
    return null;
  }

  if (size(nextState.serp.ads) === 0) {
    return null;
  }

  const serp = nextState.serp;
  const searchParameters = get(serp, 'searchParameters');
  const ads = get(serp, 'ads');
  let adsGroupedBySection = groupBy(ads, ad => get(ad, 'section_id'));

  const isSem = get(searchParameters, 'isSemUrl');
  const adsRegularLandingSection = adsGroupedBySection[SectionIds.SECTION_PAID_REGULARLANDING_ADS];

  if (isSem && !adsRegularLandingSection) {
    // TODO - This is a piece of shit !!!
    // For LandingSEM we must group ads in two sections: 97 for "main" ads and "130" for alternative ads
    // When request for the "main" ads do not return items we must report a section "99" with no ads.
    //
    // IMO the best good alternative is change the way we store ads in the redux state grouping them by section
    // and avoiding this shit here, something like: serp: { ads: { sectionA: [ads], sectionB: [ads] } }
    adsGroupedBySection = {
      ...adsGroupedBySection,
      [SectionIds.SECTION_PAID_REGULARLANDING_ADS_NORESULTS]: [
        {
          section_id: SectionIds.SECTION_PAID_REGULARLANDING_ADS_NORESULTS,
          section_type_id: SectionTypes.SECTION_TYPE_PREMIUM,
        },
      ],
    };
  }

  const boostAdsSince = get(searchParameters, 'boost_ads_since');
  const verticalName = get(nextState.config, ['verticalName']);
  const isHomesOrCarsVertical =
    verticalName === VERTICAL_NAMES.homes || verticalName === VERTICAL_NAMES.cars;
  // Empty similar content ads in SEM
  if (isSem && !boostAdsSince && isHomesOrCarsVertical) {
    adsGroupedBySection = addEmptyFewResultsSectionToAdsGroupedBySectionId(
      nextState,
      adsGroupedBySection,
      SectionIds.SECTION_PAID_SEARCH_FEWRESULTS
    );
  }
  // Empty similar content ads in organic/direct
  if (!isSem && !boostAdsSince && isHomesOrCarsVertical) {
    adsGroupedBySection = addEmptyFewResultsSectionToAdsGroupedBySectionId(
      nextState,
      adsGroupedBySection,
      SectionIds.SECTION_SEARCH_FEWRESULTS
    );
  }

  const events = map(adsGroupedBySection, section =>
    formattedSectionForAdImpressions(section, nextState)
  );

  return values(events);
};

// RELATED TO TRACKING_RELATED_ADS_POPUP
export const relatedAdsModalImpressions = (action, prevState, nextState) => ({
  hitType: 'event',
  eventCategory: TRACK_CATEGORIES.SEARCH,
  eventAction: TRACK_ACTIONS.IMPRESSION,
  eventLabel: TRACK_LABELS.RELATED_ADS,
  eventNonInteraction: true,
  ...getEventMetaData(action, prevState, nextState),
  section: action.payload
    ? SectionIds.SECTION_PAID_SEARCH_DUPLICATE
    : SectionIds.SECTION_SEARCH_DUPLICATE,
});

export const customEvent = (action, prevState, nextState) => {
  if (
    !nextState.trovit.canTriggerTrackingImpressions &&
    (action.data.action === 'impression' ||
      action.data.action === 'alertbox_impression' ||
      action.data.action === 'no_scrolled')
  ) {
    return null;
  }

  return {
    hitType: 'event',
    eventCategory: action.data.category,
    eventAction: action.data.action,
    eventLabel: action.data.label,
    eventValue: action.data.value,
    eventNonInteraction: action.data.nonInteraction || false,
    ...getEventMetaData(action, prevState, nextState),
    ...getEventSection(action),
    ...getEventAdId(action),
  };
};

export const errorEvent = action => ({
  hitType: 'exception',
  eventCategory: 'Error',
  eventAction: action.error.name,
  eventLabel: `${action.error.message} (code: ${action.error.code ||
    action.error.status ||
    'unknown'})`,
});

export const filtersChange = (action, prevState) => {
  const events = [];
  const nextFilters = get(action.data, 'filters');
  const prevFilters = getFilters(prevState);

  forEach(nextFilters, (value, key) => {
    if (value !== get(prevFilters, key)) {
      events.push({
        hitType: 'event',
        eventCategory: 'filters',
        eventAction: key,
        eventLabel: value,
      });
    }
  });

  return events.length > 0 ? events : null;
};

export const saveApply = (action, prevState, nextState) => {
  const searchParameters = nextState.serp.searchParameters;
  const config = nextState.config;
  const trovit = nextState.trovit;
  const ad = nextState.adPage.ad;

  const event = {
    hitType: 'event',
    eventCategory: TRACK_CATEGORIES.LEADS,
    eventAction: TRACK_ACTIONS.ADPAGE_SAVE_APPLY,
    eventLabel: ad.id,
    eventValue: '',
    eventMeta: {
      data: {
        type: 'leads',
        attributes: {
          site: {
            country_code: config.countryCode,
            vertical_id: config.verticalId,
          },
          section: {
            id: SectionIds.SECTION_ADPAGE_CONTACT_FORM,
          },
          ad: {
            id: ad.id,
            dealer_type_id: 1, // @todo : pending to listing api has this parameter to pass it
            source: {
              id: get(ad, ['source', 'id']),
              ad_id: get(ad, ['source', 'ad', 'id']),
            },
          },
          search: {
            type_id: get(searchParameters, 'type_id'),
          },
          user: {
            origin_id: trovit.origin,
            cookie_id: trovit.installationId,
            agent: trovit.userAgent,
            ip: trovit.userIp,
            ...action.data.user,
          },
          tracking: {
            page_view_id: trovit.pageviewId,
            ...getTrackingCode(trovit.origin, trovit.trackingCodes),
          },
          ...getABTestData(nextState.trovit.abTest),
        },
      },
    },
  };

  return event;
};

export const saveAlert = (action, prevState, nextState) => {
  const {
    res: { data, included },
    section,
  } = action.payload;
  const isNewAlert = action.payload.res.is_new_alert;
  const serp = nextState.serp;
  const config = nextState.config;
  const trovit = nextState.trovit;
  const searchParameters = get(serp, 'searchParameters');

  const alerts = included;
  const alert = alerts
    ? alerts.filter(
        a =>
          a.attributes.query.what === get(searchParameters, 'what_d') ||
          (a.attributes.query.vertical_properties &&
            a.attributes.query.vertical_properties.where === get(searchParameters, 'where'))
      )[0]
    : [];

  const origin = trovit.origin;
  const trackingCodes = trovit.trackingCodes || {};
  const tracking = trackingCodes[origin] ? { tracking_id: trackingCodes[origin] } : null;

  const dealTypeId = get(alert, 'attributes.query.vertical_properties.deal_type_id') || 1;

  const event = {
    hitType: 'event',
    eventCategory: TRACK_CATEGORIES.NOTIFICATIONS,
    eventAction: TRACK_ACTIONS.NOTIFICATIONS_SAVE_ALERT,
    eventLabel: get(searchParameters, 'what_d'),
    eventValue: '',
    eventMeta: {
      // id: ?,
      section,
      date: data.attributes.user.creation_date,
      country_code: config.countryCode,
      vertical_id: config.verticalId,
      test_ab_id: get(trovit, ['abTest', 'id']),
      test_ab_option: get(trovit, ['abTest', 'option']),
      origin_id: origin,
      browser: 1, // TODO put the correct browser id (2)
      user_agent: trovit.userAgent,
      what: get(searchParameters, 'what_d'),
      where: get(searchParameters, 'where'),
      // suggester: 0,
      type: get(searchParameters, 'type_id'),
      filters: get(searchParameters, 'filters'),
      user: data.id,
      email: data.attributes.user.email,
      alert_id: alert.id,
      alert_type: alert.type,
      alert_what: alert.attributes.query.what,
      alert_where: alert.attributes.query.vertical_properties
        ? alert.attributes.query.vertical_properties.where
        : undefined,
      // alert_suggester: ?,
      alert_filters: alert.attributes.query.filters,
      alert_site_type: dealTypeId,
      alert_vertical: alert.attributes.site.vertical_id,
      suggested: alert.attributes.notification.suggested,
      ...tracking,
      // flexible_geo: ?,
      is_new: isNewAlert,
      date_creation: data.attributes.user.creation_date,
      // frequency: ?,
      total_results: get(serp, 'count'),
      // fallback_no_results: ?,
      cookie_id: trovit.installationId,
      // google_id: ?,
      pageview_id: trovit.pageviewId,
      ip: trovit.userIp,
    },
  };

  return event;
};

export const getOpenPushNotificationEventData = (config, trovit) => ({
  data: {
    type: 'browser-events',
    attributes: {
      type: 'open',
      site: {
        country_code: config.countryCode,
        vertical_id: config.verticalId,
      },
      notification: {
        send_id: trovit.sendingId,
      },
      service_worker: {
        version: SERVICE_WORKER_VERSION,
      },
      tracking: {
        ...getTrackingCode(trovit.origin, trovit.trackingCodes),
      },
    },
    meta: {
      verticalName: config.verticalName,
      countryCode: config.countryCode,
      apiName: 'tracking',
      apiResource: 'notificationBrowserEvents',
    },
  },
});



// WEBPACK FOOTER //
// ./app/tracking/eventDefinitions.js